import { BLACK_DIE_SHAPES, type Die, type DiePosition } from "../types";

const SEAT_RADIUS = 1;

export interface Point {
  readonly x: number;
  readonly y: number;
}

function toCartesian(position: DiePosition): Point {
  return {
    x: position.radius * Math.cos(position.angle),
    y: position.radius * Math.sin(position.angle),
  };
}

/** Fixed absolute seat position for seat index `i` of `seatCount` total seats. */
export function seatPosition(seatIndex: number, seatCount: number): Point {
  const angle = ((Math.PI * 2) / seatCount) * seatIndex;
  return toCartesian({ angle, radius: SEAT_RADIUS });
}

export function seatPositionFor(
  playerId: string,
  seatingOrder: readonly string[],
): Point {
  const index = seatingOrder.indexOf(playerId);
  if (index === -1) throw new Error(`Unknown seat for player ${playerId}`);
  return seatPosition(index, seatingOrder.length);
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

const SHAPE_ORDER = new Map(
  BLACK_DIE_SHAPES.map((shape, index) => [shape, index]),
);

/**
 * The unlocked black die nearest to `playerId`'s seat (Sloth). Ties broken by
 * canonical shape order (d4 < d6 < d8 < d10 < d12 < d20) for determinism.
 */
export function nearestUnlockedDie(
  candidates: readonly Die[],
  playerId: string,
  seatingOrder: readonly string[],
): Die | null {
  if (candidates.length === 0) return null;
  const seat = seatPositionFor(playerId, seatingOrder);
  let nearest: Die | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const die of candidates) {
    const d = distance(seat, toCartesian(die.position));
    const shapeRank =
      SHAPE_ORDER.get(die.id as (typeof BLACK_DIE_SHAPES)[number]) ?? 0;
    const nearestShapeRank = nearest
      ? (SHAPE_ORDER.get(nearest.id as (typeof BLACK_DIE_SHAPES)[number]) ?? 0)
      : Number.POSITIVE_INFINITY;
    if (
      d < nearestDistance ||
      (d === nearestDistance && shapeRank < nearestShapeRank)
    ) {
      nearest = die;
      nearestDistance = d;
    }
  }
  return nearest;
}
